/* eslint-disable @typescript-eslint/no-explicit-any */

import ApiService from './api.service';
import TokenService from './User/token.service';
import {removeStorageList, getStorage, removeStorage} from '../utils/index';
import {path} from '../routes/path';
import {checkAndRefreshTokenConditionally} from '../utils/jwt';

export default class BaseApiService extends ApiService {
  constructor(props: any = null) {
    super(props);
  }

  getHeaders = (): object => ({
    ContentType: 'application/json; charset=UTF-8',
    Authorization: `Bearer ${getStorage('accessToken')}`,
    'Ocp-Apim-Subscription-Key': process.env.REACT_APP_SUBSCRIPTION_KEY,
  });

  readonly handleStatusCode = async (statusCode: any, response: any = null) => {
    if (statusCode === this.STATUS_CODE_SUCCESS) {
      removeStorage('refreshTokenAttempt');
      return response;
    } else if (statusCode === this.STATUS_CODE_FOBIDDEN) {
      window.location.href = path.DEFAULT;
    } else if (statusCode === this.STATUS_CODE_UNAUTHORIZED) {
      const accessToken = getStorage('accessToken');
      const refreshToken = getStorage('refreshToken');
      await checkAndRefreshTokenConditionally({
        accessToken,
        refreshToken,
        onFailure: async () => {},
        onTokenReachThreshold: async () => {},
        onTokenExpNotValid: async () => {},
        onTokenExpired: async () => {},
        onRefreshTokenAttemptExceed: async () => {},
        onRefreshTokenError: async () => {},
        onRefreshTokenSuccessully: async () => {},
        refreshTokenResponseCodeChecks: {
          [TokenService.STATUS_CODE_INTERNAL_SERVER_ERROR]: async () => {
            await removeStorageList([
              'accessToken',
              'refreshToken',
              'refreshTokenAttempt',
            ]);
            window.location.pathname = `/${path.AUTH}/${path.SIGN_IN}`;
          },
        },
      });
    } else {
      removeStorageList(['accessToken', 'refreshToken', 'refreshTokenAttempt']);
      window.location.pathname = `/${path.AUTH}/${path.SIGN_IN}`;
    }
  };

  getAll = async (params: object | null) => {
    return await this.get(null, params)
      .then(async res =>
        this.handleStatusCode(this.STATUS_CODE_SUCCESS, res?.data)
      )
      .catch((error: any | unknown) => {
        const {
          response: {status} = {
            status: ApiService.STATUS_CODE_INTERNAL_SERVER_ERROR,
          },
        } = error;
        return this.handleStatusCode(status);
      });
  };

  getAllByID = async (url: string, params: object | null) => {
    return await this.get(url, params)
      .then(async res =>
        this.handleStatusCode(this.STATUS_CODE_SUCCESS, res?.data)
      )
      .catch((error: any | unknown) => {
        const {
          response: {status} = {
            status: ApiService.STATUS_CODE_INTERNAL_SERVER_ERROR,
          },
        } = error;
        return this.handleStatusCode(status);
      });
  };

  getOne = async (id: number | string) => {
    return await this.get(`${id}`)
      .then(async res => this.handleStatusCode(this.STATUS_CODE_SUCCESS, res))
      .catch((error: any | unknown) => {
        const {
          response: {status} = {
            status: ApiService.STATUS_CODE_INTERNAL_SERVER_ERROR,
          },
        } = error;
        return this.handleStatusCode(status);
      });
  };

  update = async (id: number | string, params: object) => {
    return await this.patch(`${id}`, params)
      .then(async res => this.handleStatusCode(this.STATUS_CODE_SUCCESS, res))
      .catch((error: any | unknown) => {
        const {
          response: {status} = {
            status: ApiService.STATUS_CODE_INTERNAL_SERVER_ERROR,
          },
        } = error;
        return this.handleStatusCode(status);
      });
  };

  create = async (params: object) => {
    return await this.post(null, params)
      .then(async res => this.handleStatusCode(this.STATUS_CODE_SUCCESS, res))
      .catch((error: any | unknown) => {
        const {
          response: {status} = {
            status: ApiService.STATUS_CODE_INTERNAL_SERVER_ERROR,
          },
        } = error;
        return this.handleStatusCode(status);
      });
  };

  createByID = async (url: string, params: object) => {
    return await this.post(url, params)
      .then(async res => this.handleStatusCode(this.STATUS_CODE_SUCCESS, res))
      .catch((error: any | unknown) => {
        const {
          response: {status} = {
            status: ApiService.STATUS_CODE_INTERNAL_SERVER_ERROR,
          },
        } = error;
        return this.handleStatusCode(status);
      });
  };

  softDelete = async (id: number | string) => {
    return await this.delete(`${id}`)
      .then(async res => this.handleStatusCode(this.STATUS_CODE_SUCCESS, res))
      .catch((error: any | unknown) => {
        const {
          response: {status} = {
            status: ApiService.STATUS_CODE_INTERNAL_SERVER_ERROR,
          },
        } = error;
        return this.handleStatusCode(status);
      });
  };

  cashout = async (merchantId: string, params: object) => {
    return await this.post(merchantId, params)
      .then(async res => this.handleStatusCode(this.STATUS_CODE_SUCCESS, res))
      .catch((error: any | unknown) => {
        const {
          response: {status} = {
            status: ApiService.STATUS_CODE_INTERNAL_SERVER_ERROR,
          },
        } = error;
        return this.handleStatusCode(status);
      });
  };

  cashoutPeriod = async (id: string | undefined) => {
    return await this.get(`${id}/cashoutPeriod`)
      .then(async res => this.handleStatusCode(this.STATUS_CODE_SUCCESS, res))
      .catch((error: any | unknown) => {
        const {
          response: {status} = {
            status: ApiService.STATUS_CODE_INTERNAL_SERVER_ERROR,
          },
        } = error;
        return this.handleStatusCode(status);
      });
  };
}
